import { useEffect, useRef, useState } from 'react';
import { PLANETS, levelsForRegion, regionForPlanet, type SaveBlob } from '@playnets/data';
import { PlanetPortrait } from '@playnets/render-pixi';
import { resolveEvolutions } from '@playnets/sim';
import { useUi } from '../store';
import { writeSave } from '../save';

/**
 * The select screen exists to make the planets read as characters before the
 * player has looked at a single number. It runs the same body generator and
 * the same temperament motion the playfield does — so a planet's spin speed,
 * spin *direction*, how much it breathes, and whether it twitches or drifts
 * are all visible in the first two seconds.
 */
export default function PlanetSelect({
  save,
  onSave,
  onNext,
}: {
  save: SaveBlob;
  onSave: (s: SaveBlob) => void;
  onNext: () => void;
}): JSX.Element {
  const planetId = useUi((s) => s.planetId);
  const planet = PLANETS.find((p) => p.id === planetId) ?? PLANETS[0]!;
  const holderRef = useRef<HTMLDivElement | null>(null);
  const portraitRef = useRef<PlanetPortrait | null>(null);
  const [ready, setReady] = useState(false);

  const chosen = save.evolutions[planet.id] ?? [];
  const level = save.planetLevels[planet.id] ?? 1;
  const { passive, ultimate } = resolveEvolutions(planet, chosen);

  useEffect(() => {
    const holder = holderRef.current;
    if (!holder) return;
    let disposed = false;

    void PlanetPortrait.create(210).then((p) => {
      if (disposed) {
        p.destroy();
        return;
      }
      holder.appendChild(p.canvas);
      portraitRef.current = p;
      setReady(true);
    });

    return () => {
      disposed = true;
      portraitRef.current?.destroy();
      portraitRef.current = null;
      setReady(false);
    };
  }, []);

  useEffect(() => {
    if (!ready) return;
    portraitRef.current?.setPlanet(planet, chosen);
    // `chosen` is rebuilt each render; its contents are what matter here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, planet, chosen.join(',')]);

  const toggleBranch = (id: string, tier: 'l4' | 'l8'): void => {
    const siblings = planet.evolutions[tier].map((c) => c.id);
    const next = chosen.filter((c) => !siblings.includes(c));
    if (!chosen.includes(id)) next.push(id);
    const blob: SaveBlob = {
      ...save,
      evolutions: { ...save.evolutions, [planet.id]: next },
    };
    onSave(blob);
    writeSave(blob);
  };

  const unlocked = (id: string): boolean => save.unlockedPlanets.includes(id);

  return (
    <div className="menu">
      <p className="menu-eyebrow">{planet.designation}</p>
      <h1 className="menu-title">{planet.name}</h1>

      <div className="portrait-wrap" ref={holderRef} />

      <p className="planet-epithet">{planet.epithet}</p>
      <div className="traits">
        {planet.temperament.traits.map((t) => (
          <span className="trait" key={t}>
            {t}
          </span>
        ))}
      </div>

      <div className="stat">
        <div className="stat-label">Passive</div>
        <div className="stat-name">{passive.label}</div>
        <div className="stat-text">{passive.text}</div>
      </div>
      <div className="stat">
        <div className="stat-label">Ultimate · {Math.round(ultimate.cooldown)}s</div>
        <div className="stat-name">{ultimate.name}</div>
        <div className="stat-text">{ultimate.text}</div>
      </div>
      <div className="stat">
        <div className="stat-label">Home</div>
        <div className="stat-name">{regionForPlanet(planet.id).name}</div>
      </div>
      {(['l4', 'l8'] as const).map((tier) => {
        const required = tier === 'l4' ? 4 : 8;
        return (
          <div className="stat" key={tier}>
            <div className="stat-label">
              Evolution · level {required}
              {level < required ? ' · locked' : ''}
            </div>
            <div className="picker">
              {planet.evolutions[tier].map((c) => (
                <button
                  key={c.id}
                  className={`pick${chosen.includes(c.id) ? ' is-active' : ''}`}
                  disabled={level < required}
                  onClick={() => toggleBranch(c.id, tier)}
                  title={c.text}
                >
                  {c.name}
                </button>
              ))}
            </div>
            {planet.evolutions[tier].find((c) => chosen.includes(c.id)) && (
              <div className="stat-text">
                {planet.evolutions[tier].find((c) => chosen.includes(c.id))!.text}
              </div>
            )}
          </div>
        );
      })}

      <div className="picker" style={{ marginTop: 10 }}>
        {PLANETS.map((p) => (
          <button
            key={p.id}
            className={`pick${p.id === planet.id ? ' is-active' : ''}`}
            disabled={!unlocked(p.id)}
            onClick={() => useUi.getState().setPlanet(p.id)}
          >
            {unlocked(p.id) ? p.name : `${p.name} · locked`}
          </button>
        ))}
      </div>

      <button
        className="primary"
        onClick={() => {
          // Every planet has a home region, so entering the system means going
          // where this one actually lives — its palette, its enemies, its map.
          const home = regionForPlanet(planet.id);
          const first = levelsForRegion(home.id)[0];
          if (first) useUi.getState().setLevel(first.id);
          onNext();
        }}
      >
        Travel to {regionForPlanet(planet.id).name}
      </button>
    </div>
  );
}
